import { useState, useCallback, useEffect, useMemo } from 'react';

export const ACTION_COLUMNS = [
  'edit',
  'delete',
  'view',
  'details',
  'chat',
  'voice',
  'protocol',
  'actions',
  'history'
];
export const ACTION_COLUMN_WIDTH = 100;
export const DEFAULT_COLUMN_WIDTH = 150;
export const MIN_COLUMN_WIDTH = 100;

/**
 * Save a value to local storage.
 * @param {string} key
 * @param {any} value
 */
const saveToLocalStorage = (key, value) => {
  localStorage.setItem(key, JSON.stringify(value));
};

/**
 * Load a value from local storage.
 * @param {string} key
 * @returns {any}
 */
const loadFromLocalStorage = (key) => {
  const value = localStorage.getItem(key);
  return value ? JSON.parse(value) : null;
};

/**
 * Measure the width of the text.
 * @param {string} text
 * @param {string} font
 * @returns {number}
 */
const measureTextWidth = (text, font = '16px Arial') => {
  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d');
  context.font = font;
  return context.measureText(text).width;
};

/**
 * @typedef {Object} Column
 * @property {string} dataField
 * @property {string} text
 * @property {Object} [style]
 * @property {Object} [headerStyle]
 * @property {boolean} [sort]
 */

/**
 * @typedef {Object} ColumnConfig
 * @property {string} key
 * @property {number} position
 * @property {number} size
 * @property {boolean} isSort
 * @property {string|null} sortDirection
 * @property {boolean} visible
 */

/**
 * @typedef {Object} SortConfig
 * @property {string|null} key
 * @property {string|null} direction
 */

/**
 * Custom hook to manage column order and widths.
 * @param {Column[]} columns
 * @param {React.Dispatch<React.SetStateAction<SortConfig>>} setSortConfig
 * @returns {{
 *   columnOrder: Column[],
 *   columnWidths: number[],
 *   minWidths: number[],
 *   handleResize: (index: number, width: number) => void,
 *   moveColumn: (dragIndex: number, hoverIndex: number) => void,
 *   requestSort: (key: string) => void,
 *   resetFilter: () => void,
 *   visibleColumns: string[],
 *   handleColumnVisibilityChange: (dataField: string) => void,
 *   sortConfig: SortConfig
 * }}
 */
export const useColumnOrder = (columns, setSortConfig, entityName) => {
  const pathKey = entityName;
  const LOCAL_STORAGE_KEY = `flexTableConfig::[${pathKey}]`;

  const [columnConfigs, setColumnConfigs] = useState(() => {
    const savedConfig = loadFromLocalStorage(LOCAL_STORAGE_KEY);
    if (Array.isArray(savedConfig)) {
      return savedConfig;
    }
    return columns.map((col, index) => {
      const textWidth = measureTextWidth(col.text) + 20; // Add some padding
      return {
        key: col.dataField,
        position: index,
        size: Math.max(
          ACTION_COLUMNS.includes(col.dataField)
            ? ACTION_COLUMN_WIDTH
            : DEFAULT_COLUMN_WIDTH,
          textWidth
        ),
        isSort: false,
        sortDirection: null,
        visible: true
      };
    });
  });

  const columnOrder = useMemo(
    () =>
      columnConfigs
        .sort((a, b) => a.position - b.position)
        .map((config) => {
          const column = columns.find((col) => col.dataField === config.key);
          return column ? column : { dataField: config.key, text: config.key };
        }),
    [columnConfigs, columns]
  );

  const columnWidths = useMemo(
    () => columnConfigs.map((config) => config.size),
    [columnConfigs]
  );

  const visibleColumns = useMemo(
    () =>
      columnConfigs
        .filter(
          (config) => config.visible || ACTION_COLUMNS.includes(config.key)
        )
        .map((config) => config.key),
    [columnConfigs]
  );

  const sortConfig = useMemo(
    () =>
      columnConfigs.find((config) => config.isSort) || {
        key: null,
        direction: null
      },
    [columnConfigs]
  );

  const minWidths = useMemo(
    () =>
      columns.map((col) =>
        ACTION_COLUMNS.includes(col.dataField)
          ? ACTION_COLUMN_WIDTH
          : MIN_COLUMN_WIDTH
      ),
    [columns]
  );

  useEffect(() => {
    const savedConfig = loadFromLocalStorage(LOCAL_STORAGE_KEY);
    if (Array.isArray(savedConfig)) {
      setColumnConfigs(savedConfig);
    }
  }, [columns]);

  const moveColumn = useCallback(
    (dragIndex, hoverIndex) => {
      if (
        ACTION_COLUMNS.includes(columnOrder[dragIndex].dataField) ||
        ACTION_COLUMNS.includes(columnOrder[hoverIndex].dataField)
      ) {
        return;
      }

      setColumnConfigs((prevConfigs) => {
        const newConfigs = [...prevConfigs];
        const [draggedConfig] = newConfigs.splice(dragIndex, 1);
        newConfigs.splice(hoverIndex, 0, draggedConfig);

        newConfigs.forEach((config, index) => {
          config.position = index;
        });

        saveToLocalStorage(LOCAL_STORAGE_KEY, newConfigs);
        return newConfigs;
      });
    },
    [columnOrder]
  );

  const handleResize = useCallback(
    (index, width) => {
      setColumnConfigs((prevConfigs) => {
        const newConfigs = [...prevConfigs];
        newConfigs[index].size = Math.max(width, minWidths[index]);
        saveToLocalStorage(LOCAL_STORAGE_KEY, newConfigs);
        return newConfigs;
      });
    },
    [minWidths]
  );

  const requestSort = useCallback(
    (key) => {
      setColumnConfigs((prevConfigs) => {
        const newConfigs = prevConfigs.map((config) => {
          if (config.key === key) {
            return {
              ...config,
              isSort: true,
              sortDirection:
                config.sortDirection === 'ascending'
                  ? 'descending'
                  : 'ascending'
            };
          }
          return { ...config, isSort: false, sortDirection: null };
        });

        const sortConfig = newConfigs.find((config) => config.isSort);
        setSortConfig({
          key: sortConfig ? sortConfig.key : null,
          direction: sortConfig ? sortConfig.sortDirection : null
        });

        saveToLocalStorage(LOCAL_STORAGE_KEY, newConfigs);
        return newConfigs;
      });
    },
    [setSortConfig]
  );

  const handleColumnVisibilityChange = useCallback((dataField) => {
    if (ACTION_COLUMNS.includes(dataField)) return;

    setColumnConfigs((prevConfigs) => {
      const newConfigs = prevConfigs.map((config) =>
        config.key === dataField
          ? { ...config, visible: !config.visible }
          : config
      );
      saveToLocalStorage(LOCAL_STORAGE_KEY, newConfigs);
      return newConfigs;
    });
  }, []);

  const resetFilter = useCallback(() => {
    const initialConfigs = columns.map((col, index) => {
      const textWidth = measureTextWidth(col.text) + 20; // Add some padding
      return {
        key: col.dataField,
        position: index,
        size: Math.max(
          ACTION_COLUMNS.includes(col.dataField)
            ? ACTION_COLUMN_WIDTH
            : DEFAULT_COLUMN_WIDTH,
          textWidth
        ),
        isSort: false,
        sortDirection: null,
        visible: true
      };
    });
    setColumnConfigs(initialConfigs);
    setSortConfig({ key: null, direction: null });
    localStorage.removeItem(LOCAL_STORAGE_KEY);
  }, [columns, setSortConfig]);

  return {
    columnOrder,
    columnWidths,
    minWidths,
    handleResize,
    moveColumn,
    requestSort,
    resetFilter,
    visibleColumns,
    handleColumnVisibilityChange,
    sortConfig
  };
};
